import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
    Facebook,
    Instagram,
    Mail,
    MapPin,
    Menu,
    Phone,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button'
import { Logo } from '@/components/generals/logo'
import { WhatsAppIcon } from '@/components/generals/icons'
import { contactInfo, navLinks, whatsappLink } from '@/constants/site'

const isActive = (pathname: string, href: string): boolean => {
    if (href === '/') return pathname === '/'
    return pathname === href || pathname.startsWith(`${href}/`)
}

export const Header: FC = (): ReactNode => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const toggleRef = useRef<HTMLButtonElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 24)
        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    const closeMenu = useCallback((): void => {
        setMenuOpen(false)
        toggleRef.current?.focus()
    }, [])

    useEffect(() => {
        const onRouteChange = (): void => setMenuOpen(false)
        router.events.on('routeChangeStart', onRouteChange)
        return () => router.events.off('routeChangeStart', onRouteChange)
    }, [router.events])

    useEffect(() => {
        if (!menuOpen) return

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        const panel = panelRef.current
        const focusable = panel?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
        focusable?.[0]?.focus()

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                closeMenu()
                return
            }

            if (event.key !== 'Tab' || !focusable || focusable.length === 0) return

            const first = focusable[0]
            const last = focusable[focusable.length - 1]

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        document.addEventListener('keydown', onKeyDown)

        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [menuOpen, closeMenu])

    return (
        <header className='fixed inset-x-0 top-0 z-50'>
            <div
                className={cn(
                    'hidden overflow-hidden border-b border-white/10 bg-night text-white/70 transition-all duration-500 lg:block',
                    scrolled ? 'max-h-0 opacity-0' : 'max-h-11 opacity-100'
                )}
            >
                <div className='mx-auto flex h-10 max-w-7xl items-center justify-between px-6 text-xs'>
                    <div className='flex items-center gap-6'>
                        <a href={contactInfo.phoneHref} className='focus-ring inline-flex items-center gap-2 rounded transition-colors hover:text-white'>
                            <Phone className='size-3.5 text-gold' aria-hidden='true' />
                            {contactInfo.phoneDisplay}
                        </a>

                        <a href={`mailto:${contactInfo.email}`} className='focus-ring inline-flex items-center gap-2 rounded transition-colors hover:text-white'>
                            <Mail className='size-3.5 text-gold' aria-hidden='true' />
                            {contactInfo.email}
                        </a>

                        <span className='inline-flex items-center gap-2'>
                            <MapPin className='size-3.5 text-gold' aria-hidden='true' />
                            {contactInfo.addressShort}
                        </span>
                    </div>

                    <div className='flex items-center gap-4'>
                        <span className='text-white/50'>
                            Follow us
                        </span>

                        <a href={contactInfo.instagram} target='_blank' rel='noopener noreferrer' aria-label='Guju Events on Instagram' className='focus-ring rounded transition-colors hover:text-white'>
                            <Instagram className='size-4' aria-hidden='true' />
                        </a>

                        <a href={contactInfo.facebook} target='_blank' rel='noopener noreferrer' aria-label='Guju Events on Facebook' className='focus-ring rounded transition-colors hover:text-white'>
                            <Facebook className='size-4' aria-hidden='true' />
                        </a>

                        <a href={whatsappLink()} target='_blank' rel='noopener noreferrer' aria-label='Chat with Guju Events on WhatsApp' className='focus-ring rounded transition-colors hover:text-white'>
                            <WhatsAppIcon className='size-4' />
                        </a>
                    </div>
                </div>
            </div>

            <div
                className={cn(
                    'border-b transition-all duration-500',
                    scrolled ? 'border-white/10 bg-night/90 backdrop-blur-xl' : 'border-transparent bg-transparent'
                )}
            >
                <nav aria-label='Main' className={cn('mx-auto flex max-w-7xl items-center justify-between px-6 transition-all duration-500', scrolled ? 'h-18' : 'h-20 lg:h-22')}>
                    <Logo />

                    <ul className='hidden items-center gap-1 lg:flex'>
                        {navLinks.map((link) => {
                            const active = isActive(router.pathname, link.href)

                            return (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        aria-current={active ? 'page' : undefined}
                                        className={cn(
                                            'focus-ring relative rounded-full px-4 py-2 text-sm font-medium transition-colors',
                                            active ? 'text-white' : 'text-white/70 hover:text-white'
                                        )}
                                    >
                                        {link.label}

                                        <span
                                            aria-hidden='true'
                                            className={cn(
                                                'absolute inset-x-0 -bottom-0.5 mx-auto h-0.5 w-5 rounded-full bg-gold transition-opacity',
                                                active ? 'opacity-100' : 'opacity-0'
                                            )}
                                        />
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>

                    <div className='flex items-center gap-3'>
                        <ButtonLink href='/contact#inquiry' size='md' className='hidden sm:inline-flex'>
                            Make an inquiry
                        </ButtonLink>

                        <button
                            ref={toggleRef}
                            type='button'
                            onClick={() => setMenuOpen(true)}
                            aria-expanded={menuOpen}
                            aria-controls='mobile-menu'
                            aria-label='Open menu'
                            className='focus-ring inline-flex size-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:bg-white/10 lg:hidden'
                        >
                            <Menu className='size-5' aria-hidden='true' />
                        </button>
                    </div>
                </nav>
            </div>

            {menuOpen && (
                <div
                    id='mobile-menu'
                    ref={panelRef}
                    role='dialog'
                    aria-modal='true'
                    aria-label='Menu'
                    className='surface-night fixed inset-0 z-60 flex flex-col overflow-y-auto px-6 pt-5 pb-10 text-white animate-in fade-in duration-300 lg:hidden'
                >
                    <div className='flex items-center justify-between'>
                        <Logo />

                        <button
                            type='button'
                            onClick={closeMenu}
                            aria-label='Close menu'
                            className='focus-ring inline-flex size-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:bg-white/10'
                        >
                            <X className='size-5' aria-hidden='true' />
                        </button>
                    </div>

                    <ul className='mt-12 flex flex-col gap-1'>
                        {navLinks.map((link, index) => {
                            const active = isActive(router.pathname, link.href)

                            return (
                                <li key={link.href} className='animate-in fade-in slide-in-from-bottom-3 fill-mode-both' style={{ animationDelay: `${index * 50}ms` }}>
                                    <Link
                                        href={link.href}
                                        onClick={() => setMenuOpen(false)}
                                        aria-current={active ? 'page' : undefined}
                                        className={cn(
                                            'focus-ring flex items-baseline justify-between rounded-lg py-3 font-heading text-4xl transition-colors',
                                            active ? 'text-gold' : 'text-white hover:text-gold-soft'
                                        )}
                                    >
                                        {link.label}

                                        <span className='font-sans text-xs tracking-[0.2em] text-white/40'>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>

                    <div className='mt-auto flex flex-col gap-3 pt-10'>
                        <ButtonLink href='/contact#inquiry' size='lg' onClick={() => setMenuOpen(false)}>
                            Make an inquiry
                        </ButtonLink>

                        <div className='grid grid-cols-2 gap-3'>
                            <a href={contactInfo.phoneHref} className='focus-ring inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/20 text-sm font-semibold'>
                                <Phone className='size-4' aria-hidden='true' />
                                Call us
                            </a>

                            <a href={whatsappLink()} target='_blank' rel='noopener noreferrer' className='focus-ring inline-flex h-12 items-center justify-center gap-2 rounded-full bg-whatsapp text-sm font-semibold'>
                                <WhatsAppIcon className='size-4' />
                                WhatsApp
                            </a>
                        </div>

                        <p className='pt-4 text-center text-xs text-white/50'>
                            {contactInfo.addressFull}
                        </p>
                    </div>
                </div>
            )}
        </header>
    )
}
